// src/components/roads/RoadInspectionChecklist.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Road inspection checklist (tick boxes, measurements, notes,
// feedback) with a FlowSense AI helper.
//
//   <RoadInspectionChecklist isOpen onClose roadId roadName road? workOrder?
//        checklistId? category? onSubmitted? />
//
//   • Templates come from the API (GET /roads/inspection-checklists/templates) —
//     routine survey, pothole / paving QA, line marking, drainage, sidewalk, signage;
//     picked automatically from the work order category.
//   • Each item: ✓ OK tick box · ✗ Defect · N/A · measurement (auto-fails outside the
//     spec) · note (required for failed critical items).
//   • Auto-saves a draft to the server (debounced) + a local backup, resumes the last
//     draft for the same road / work order. Submitted checklists are read-only.
//   • AI helper: live score, what's missing, suggested follow-up jobs, and
//     "Ask FlowSense AI" (Ollama) with a rules fallback.
//   • Submit → server scores it, records the condition, may raise a repair job
//     automatically; follow-ups open the work order form pre-filled (typed).
// --- UPDATE (Inspection photos v1) -------------------------------------------
//   • Field photos: shows the photos the FlowSense Tech (mobile) app uploaded for
//     this inspection — GET /roads/inspection-checklists/:id/photos.
//       – Per item: thumbnails under the checklist item they were taken for
//         (item_id), plus a photo count on each section header.
//       – General photos (no item) and, for a work order, the job photos
//         (before / during / after — GET /roads/work-orders/:id/photos) in an
//         "Inspection photos" gallery.
//       – Lightbox: full image, ← / → keys, caption, item, stage, taken / uploaded
//         time, GPS (opens the map), uploader, file size, open original.
//   • Stays current: refreshes on the work order / road WebSocket events, every 20 s
//     while the inspection is still a draft (the inspector may still be uploading),
//     when the browser tab comes back into focus, and with a Refresh button.
//   • AI helper: warns about failed critical items with no photo, and passes the
//     photo counts to FlowSense AI. Nothing is invented — a missing value shows "—".
//   • Photo URLs are stored relative (/uploads/road_photos/…) and served by the
//     API server, so they are prefixed with the API origin.
// --- END UPDATE --------------------------------------------------------------
// --- UPDATE (Completed work): PhotoThumb and PhotoLightbox are exported (used by the Road
//   Works Report "Completed work" tab); PhotoLightbox takes an optional metaFor(photo).
// --- END UPDATE --------------------------------------------------------------
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Box, Flex, HStack, VStack, Text, Badge, Button, IconButton, Checkbox, Input, Textarea, Select,
    Progress, Tooltip, Icon, Collapse, Alert, AlertIcon, AlertDescription, Spinner, SimpleGrid,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Tag, Wrap, WrapItem,
    InputGroup, InputRightAddon, useColorModeValue, useToast, useBreakpointValue,
} from '@chakra-ui/react';
import { MdClose, MdNotes, MdAutoAwesome, MdSave, MdSend, MdStar, MdStarBorder, MdBuild, MdDoneAll, MdRefresh, MdLock } from 'react-icons/md';
// --- UPDATE (Inspection photos v1)
import { MdPhotoCamera, MdChevronLeft, MdChevronRight, MdPlace, MdOpenInNew, MdBrokenImage } from 'react-icons/md';
import webSocketService from '../../services/websocketService';
// --- END UPDATE
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './page/shared';
// --- UPDATE (Inspection photos v1)
import { API_BASE } from './page/shared';
// --- END UPDATE
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import { openCreateWorkOrder } from '../work_orders/workOrderCatalog';
import { publish as publishBus, BUS_EVENTS } from '../../utils/flowsenseBus';
import {
    scoreChecklist, checklistAdvice, itemOutcome, buildRoadAiContext, roadFollowUpDraft, CRITICAL_CAP,
} from './roadAiIntel';

const AUTOSAVE_MS = 1500;
const RATING_COLOR = { excellent: 'green', good: 'teal', fair: 'yellow', poor: 'orange', critical: 'red' };
const ADVICE_STATUS = { warning: 'warning', error: 'error', info: 'info', success: 'success' };

// Templates are static per deployment — fetch once per page load.
let templatesPromise = null;
export const loadChecklistTemplates = () => {
    if (!templatesPromise) {
        templatesPromise = apiFetch('/roads/inspection-checklists/templates')
            .then((res) => {
                const d = res?.data || res;
                const list = d?.templates || (Array.isArray(d) ? d : []);
                if (!list.length) throw new Error('No checklist templates returned.');
                return { templates: list, criticalCap: d?.critical_cap ?? CRITICAL_CAP };
            })
            .catch((err) => { templatesPromise = null; throw err; });
    }
    return templatesPromise;
};

/** Same mapping as the backend templateForCategory(). */
export const templateKeyFor = (templates, category) => {
    const c = String(category || '').trim().toLowerCase();
    if (!c) return 'routine';
    const hit = templates.find((t) => t.key === c || (t.appliesTo || []).some((a) => a.toLowerCase() === c));
    if (hit) return hit.key;
    if (/pothole/.test(c)) return 'pothole_repair';
    if (/pav|resurf|overlay|reinstat/.test(c)) return 'paving_qa';
    if (/strip|marking|crosswalk|stop bar/.test(c)) return 'line_marking';
    if (/drain|culvert|gully/.test(c)) return 'drainage';
    if (/sidewalk|curb|kerb/.test(c)) return 'sidewalk_curb';
    if (/sign/.test(c)) return 'signage';
    return 'routine';
};

const backupKey = (roadId, woId) => `flowSense_roadChecklist_${roadId}_${woId || 'none'}`;
const readBackup = (key) => { try { return JSON.parse(window.localStorage.getItem(key) || 'null'); } catch { return null; } };
const writeBackup = (key, v) => { try { window.localStorage.setItem(key, JSON.stringify(v)); } catch { /* quota / private mode */ } };
const clearBackup = (key) => { try { window.localStorage.removeItem(key); } catch { /* ignore */ } };

// --- UPDATE (Inspection photos v1) -------------------------------------------
const PHOTO_POLL_MS = 20000;
const NO_PHOTOS = Object.freeze([]);
const STAGE_COLOR = { before: 'orange', during: 'blue', after: 'green', inspection: 'purple' };
const STAGE_LABEL = { before: 'Before', during: 'During', after: 'After', inspection: 'Inspection' };
const API_ORIGIN = String(API_BASE || '').replace(/\/api(\/v\d+)?\/?$/, '');

/** Absolute URL of a stored photo (/uploads/... or already absolute). */
export const photoUrl = (url) => {
    if (!url) return null;
    if (/^(https?:|data:|blob:)/i.test(url)) return url;
    return `${API_ORIGIN}${String(url).startsWith('/') ? '' : '/'}${url}`;
};
const fmtWhen = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const fmtBytes = (n) => {
    if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
    const b = Number(n);
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const hasGps = (p) => Number.isFinite(p?.latitude) && Number.isFinite(p?.longitude) && !(p.latitude === 0 && p.longitude === 0);
const photoTime = (p) => new Date(p?.taken_at || p?.created_at || 0).getTime() || 0;
const listOf = (res) => {
    const d = res?.data ?? res;
    return Array.isArray(d) ? d : (Array.isArray(d?.photos) ? d.photos : []);
};

/** Thumbnail that degrades to a placeholder when the file is missing on the server. */
export const PhotoThumb = memo(({ photo, size, onOpen, label, badge, colors }) => { // --- UPDATE (Completed work): exported
    const [broken, setBroken] = useState(false);
    const src = photoUrl(photo.url);
    return (
        <Box
            as="button"
            type="button"
            onClick={() => onOpen(photo)}
            position="relative"
            w={size}
            h={size === '100%' ? 'auto' : size}
            sx={{ aspectRatio: '1 / 1' }}
            flexShrink={0}
            borderRadius="md"
            overflow="hidden"
            borderWidth="1px"
            borderColor={colors.border}
            bg={colors.thumbBg}
            _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }}
            _focusVisible={{ boxShadow: 'outline' }}
            transition="all 0.15s"
            aria-label={`Open photo${label ? ` — ${label}` : ''}`}
            data-testid={`photo-${photo.id}`}
        >
            {broken || !src ? (
                <Flex w="100%" h="100%" align="center" justify="center" direction="column" color={colors.muted}>
                    <Icon as={MdBrokenImage} boxSize={5} />
                    <Text fontSize="2xs">No image</Text>
                </Flex>
            ) : (
                <img
                    src={src}
                    alt={photo.caption || label || 'Inspection photo'}
                    loading="lazy"
                    decoding="async"
                    onError={() => setBroken(true)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            )}
            {badge && (
                <Badge position="absolute" bottom={1} left={1} fontSize="2xs" colorScheme={STAGE_COLOR[photo.stage] || 'gray'} variant="solid">
                    {badge}
                </Badge>
            )}
        </Box>
    );
});
PhotoThumb.displayName = 'PhotoThumb';
PhotoThumb.propTypes = { photo: PropTypes.object.isRequired, size: PropTypes.any, onOpen: PropTypes.func.isRequired, label: PropTypes.string, badge: PropTypes.string, colors: PropTypes.object.isRequired };

/** Full-size viewer with prev / next and the photo's field metadata. */
export function PhotoLightbox({ photos, index, onClose, onIndex, itemLabels, uploaderName, colors, metaFor }) { // --- UPDATE (Completed work): exported, + metaFor(photo) → [label, value] for the first row
    const photo = index !== null && index !== undefined ? photos[index] : null;
    const [broken, setBroken] = useState(false);
    useEffect(() => { setBroken(false); }, [photo?.id]);
    const count = photos.length;
    const go = useCallback((step) => { if (count) onIndex((index + step + count) % count); }, [count, index, onIndex]);
    useEffect(() => {
        if (!photo) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [photo, go]);
    if (!photo) return null;
    const src = photoUrl(photo.url);
    const item = photo.item_id ? itemLabels[photo.item_id] : null;
    const meta = [
        (metaFor && metaFor(photo)) || ['Checklist item', item || (photo.item_id ? photo.item_id : 'General inspection photo')], // --- UPDATE (Completed work)
        ['Stage', STAGE_LABEL[photo.stage] || photo.stage || '—'],
        ['Taken', fmtWhen(photo.taken_at)],
        ['Uploaded', fmtWhen(photo.created_at)],
        ['Uploaded by', uploaderName(photo.uploaded_by_id)],
        ['File size', fmtBytes(photo.size_bytes)],
    ];
    return (
        <Modal isOpen onClose={onClose} size="6xl" isCentered>
            <ModalOverlay bg="blackAlpha.800" />
            <ModalContent bg={colors.lightboxBg} borderRadius="xl" overflow="hidden" data-testid="photo-lightbox">
                <ModalCloseButton zIndex={2} />
                <Flex direction={{ base: 'column', md: 'row' }} minH={{ md: '60vh' }}>
                    <Flex flex="1" position="relative" align="center" justify="center" bg="black" minH="280px">
                        {broken || !src ? (
                            <VStack color="gray.400" py={16}><Icon as={MdBrokenImage} boxSize={10} /><Text fontSize="sm">The image file could not be loaded.</Text></VStack>
                        ) : (
                            <img src={src} alt={photo.caption || item || 'Inspection photo'} onError={() => setBroken(true)}
                                style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', display: 'block' }} />
                        )}
                        {count > 1 && (
                            <>
                                <IconButton icon={<Icon as={MdChevronLeft} boxSize={7} />} aria-label="Previous photo" position="absolute" left={2} top="50%" transform="translateY(-50%)" borderRadius="full" onClick={() => go(-1)} />
                                <IconButton icon={<Icon as={MdChevronRight} boxSize={7} />} aria-label="Next photo" position="absolute" right={2} top="50%" transform="translateY(-50%)" borderRadius="full" onClick={() => go(1)} />
                            </>
                        )}
                    </Flex>
                    <Box w={{ base: '100%', md: '300px' }} p={4} flexShrink={0}>
                        <Text fontSize="xs" color={colors.muted} mb={1}>Photo {index + 1} of {count}</Text>
                        <Text fontWeight="800" fontSize="sm" mb={2}>{photo.caption || item || 'Inspection photo'}</Text>
                        <VStack align="stretch" spacing={1.5} fontSize="xs">
                            {meta.map(([k, v]) => (
                                <Flex key={k} justify="space-between" gap={3}>
                                    <Text color={colors.muted}>{k}</Text>
                                    <Text fontWeight="600" textAlign="right">{v}</Text>
                                </Flex>
                            ))}
                            <Flex justify="space-between" gap={3}>
                                <Text color={colors.muted}>Location</Text>
                                {hasGps(photo) ? (
                                    <Button as="a" href={`https://www.google.com/maps?q=${photo.latitude},${photo.longitude}`} target="_blank" rel="noopener noreferrer"
                                        size="xs" variant="link" leftIcon={<Icon as={MdPlace} />}>
                                        {photo.latitude.toFixed(5)}, {photo.longitude.toFixed(5)}
                                    </Button>
                                ) : <Text fontWeight="600">—</Text>}
                            </Flex>
                        </VStack>
                        {src && (
                            <Button as="a" href={src} target="_blank" rel="noopener noreferrer" size="xs" mt={4} leftIcon={<Icon as={MdOpenInNew} />} variant="outline">
                                Open original
                            </Button>
                        )}
                    </Box>
                </Flex>
            </ModalContent>
        </Modal>
    );
}
PhotoLightbox.propTypes = {
    photos: PropTypes.array.isRequired, index: PropTypes.number, onClose: PropTypes.func.isRequired, onIndex: PropTypes.func.isRequired,
    itemLabels: PropTypes.object.isRequired, uploaderName: PropTypes.func.isRequired, colors: PropTypes.object.isRequired,
    metaFor: PropTypes.func, // --- UPDATE (Completed work)
};
// --- END UPDATE --------------------------------------------------------------

function responsesReducer(state, action) {
    switch (action.type) {
        case 'reset': return action.value || {};
        case 'set': {
            const prev = state[action.id] || {};
            const next = { ...prev, ...action.patch };
            if (action.patch.status && action.patch.status !== prev.status) next.checked_at = new Date().toISOString();
            return { ...state, [action.id]: next };
        }
        case 'bulk': {
            const next = { ...state };
            action.ids.forEach((id) => { if (!next[id]?.status) next[id] = { ...(next[id] || {}), status: action.status, checked_at: new Date().toISOString() }; });
            return next;
        }
        default: return state;
    }
}

/** One checklist row. Memoised — typing in one note re-renders only that row. */
const ChecklistItemRow = memo(({ item, response, onChange, readOnly, colors, photos = NO_PHOTOS, onOpenPhoto, photosLoaded }) => { // --- UPDATE (Inspection photos v1): photos, onOpenPhoto, photosLoaded
    const outcome = itemOutcome(item, response);
    const status = outcome.status;
    const [showNote, setShowNote] = useState(!!response?.note);
    const noteRequired = item.critical && status === 'fail' && !String(response?.note || '').trim();
    const m = item.measurement;
    const limit = m ? [m.min !== undefined ? `≥ ${m.min}` : null, m.max !== undefined ? `≤ ${m.max}` : null].filter(Boolean).join(' and ') : '';
    const set = (patch) => onChange(item.id, patch);
    const bg = status === 'fail' ? colors.failBg : status === 'pass' ? colors.passBg : 'transparent';
    return (
        <Box px={3} py={2.5} borderRadius="lg" bg={bg} borderWidth="1px" borderColor={status === 'fail' ? colors.failBorder : colors.border} data-testid={`item-${item.id}`}>
            <Flex align="flex-start" gap={3} wrap={{ base: 'wrap', md: 'nowrap' }}>
                <Checkbox
                    isChecked={status === 'pass'}
                    isDisabled={readOnly}
                    colorScheme="green"
                    size="lg"
                    mt={0.5}
                    onChange={(e) => set({ status: e.target.checked ? 'pass' : null })}
                    aria-label={`${item.label} OK`}
                />
                <Box flex="1" minW="180px">
                    <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize="sm" fontWeight="600">{item.label}</Text>
                        {item.critical && <Badge colorScheme="red" fontSize="2xs" variant="subtle">critical</Badge>}
                        {outcome.outOfRange && <Badge colorScheme="red" fontSize="2xs">out of spec</Badge>}
                        {/* --- UPDATE (Inspection photos v1) */}
                        {photos.length > 0 && (
                            <Badge colorScheme="purple" fontSize="2xs" variant="subtle" aria-label={`${photos.length} photo(s)`}>
                                <Icon as={MdPhotoCamera} mr={0.5} verticalAlign="-1px" />{photos.length}
                            </Badge>
                        )}
                        {/* --- END UPDATE */}
                    </HStack>
                    {item.help && <Text fontSize="xs" color={colors.muted}>{item.help}</Text>}
                    {m && (
                        <HStack mt={1.5} spacing={2}>
                            <InputGroup size="xs" maxW="200px">
                                <Input
                                    type="number"
                                    inputMode="decimal"
                                    step="any"
                                    value={response?.measurement ?? ''}
                                    onChange={(e) => set({ measurement: e.target.value === '' ? null : e.target.value })}
                                    placeholder={m.label}
                                    isReadOnly={readOnly}
                                    aria-label={`${item.label} — ${m.label}`}
                                    isInvalid={outcome.outOfRange}
                                />
                                <InputRightAddon>{m.unit}</InputRightAddon>
                            </InputGroup>
                            {limit && <Text fontSize="2xs" color={colors.muted}>spec {limit} {m.unit}</Text>}
                        </HStack>
                    )}
                </Box>
                <HStack spacing={1} flexShrink={0}>
                    <Tooltip label="Defect found" hasArrow>
                        <Button size="xs" leftIcon={<Icon as={MdClose} />} colorScheme="red" variant={status === 'fail' ? 'solid' : 'outline'} isDisabled={readOnly}
                            onClick={() => { set({ status: status === 'fail' && !outcome.outOfRange ? null : 'fail' }); setShowNote(true); }}
                            aria-pressed={status === 'fail'} aria-label={`${item.label} defect`}>
                            Defect
                        </Button>
                    </Tooltip>
                    <Button size="xs" variant={status === 'na' ? 'solid' : 'ghost'} isDisabled={readOnly} onClick={() => set({ status: status === 'na' ? null : 'na' })} aria-pressed={status === 'na'} aria-label={`${item.label} not applicable`}>
                        N/A
                    </Button>
                    <Tooltip label={showNote ? 'Hide note' : 'Add a note'} hasArrow>
                        <IconButton size="xs" variant={response?.note ? 'solid' : 'ghost'} icon={<Icon as={MdNotes} />} onClick={() => setShowNote((v) => !v)} aria-label={`Note for ${item.label}`} />
                    </Tooltip>
                </HStack>
            </Flex>
            <Collapse in={showNote || noteRequired} animateOpacity>
                <Textarea
                    mt={2}
                    size="sm"
                    rows={2}
                    value={response?.note || ''}
                    onChange={(e) => set({ note: e.target.value })}
                    placeholder={status === 'fail' ? 'What did you find? Location (chainage), size, photos…' : 'Note (optional)'}
                    isReadOnly={readOnly}
                    isInvalid={noteRequired}
                    maxLength={2000}
                    aria-label={`Note for ${item.label}`}
                />
                {noteRequired && <Text fontSize="2xs" color="red.500" mt={0.5}>A note is required for a failed critical item.</Text>}
            </Collapse>
            {/* --- UPDATE (Inspection photos v1): photos taken for this item */}
            {photos.length > 0 && onOpenPhoto && (
                <HStack mt={2} spacing={2} overflowX="auto" pb={1} pl={{ base: 0, md: 9 }} data-testid={`item-photos-${item.id}`}>
                    {photos.map((p) => (
                        <PhotoThumb key={p.id} photo={p} size="64px" onOpen={onOpenPhoto} label={item.label} colors={colors} />
                    ))}
                </HStack>
            )}
            {photosLoaded && photos.length === 0 && item.critical && status === 'fail' && (
                <Text fontSize="2xs" color="orange.500" mt={1} pl={{ base: 0, md: 9 }}>
                    <Icon as={MdPhotoCamera} mr={1} verticalAlign="-1px" />No photo has been uploaded for this failed critical item.
                </Text>
            )}
            {/* --- END UPDATE */}
        </Box>
    );
});
ChecklistItemRow.displayName = 'ChecklistItemRow';
ChecklistItemRow.propTypes = { item: PropTypes.object.isRequired, response: PropTypes.object, onChange: PropTypes.func.isRequired, readOnly: PropTypes.bool, colors: PropTypes.object.isRequired };
// --- UPDATE (Inspection photos v1)
// Production builds strip `X.propTypes = …` assignments, so never mutate .propTypes — reassign it.
ChecklistItemRow.propTypes = {
    ...(ChecklistItemRow.propTypes || {}),
    photos: PropTypes.array,
    onOpenPhoto: PropTypes.func,
    photosLoaded: PropTypes.bool,
};
// --- END UPDATE

function StarRating({ value, onChange, readOnly }) {
    return (
        <HStack spacing={0.5} role="radiogroup" aria-label="Overall rating">
            {[1, 2, 3, 4, 5].map((n) => (
                <IconButton key={n} size="sm" variant="ghost" color={value >= n ? 'yellow.400' : 'gray.400'} icon={<Icon as={value >= n ? MdStar : MdStarBorder} boxSize={5} />}
                    onClick={() => !readOnly && onChange(value === n ? null : n)} aria-label={`${n} star${n > 1 ? 's' : ''}`} role="radio" aria-checked={value === n} isDisabled={readOnly} />
            ))}
        </HStack>
    );
}
StarRating.propTypes = { value: PropTypes.number, onChange: PropTypes.func.isRequired, readOnly: PropTypes.bool };

export default function RoadInspectionChecklist({ isOpen, onClose, roadId, roadName, road, workOrder, checklistId, category, onSubmitted }) {
    const toast = useToast();
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, lg: false });
    const muted = useColorModeValue('gray.500', 'gray.400');
    const border = useColorModeValue('gray.200', 'gray.600');
    const passBg = useColorModeValue('green.50', 'rgba(72,187,120,0.10)');
    const failBg = useColorModeValue('red.50', 'rgba(245,101,101,0.10)');
    const failBorder = useColorModeValue('red.200', 'red.700');
    const aiBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.12)');
    // --- UPDATE (Inspection photos v1): photo colours (hoisted)
    const thumbBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const lightboxBg = useColorModeValue('white', 'gray.800');
    const galleryBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    // --- END UPDATE
    const colors = useMemo(() => ({ muted, border, passBg, failBg, failBorder, thumbBg, lightboxBg }), [muted, border, passBg, failBg, failBorder, thumbBg, lightboxBg]); // --- UPDATE: + thumbBg, lightboxBg

    const [templates, setTemplates] = useState(null);
    const [criticalCap, setCriticalCap] = useState(CRITICAL_CAP);
    const [loadError, setLoadError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [templateKey, setTemplateKey] = useState('routine');
    const [responses, dispatch] = useReducer(responsesReducer, {});
    const [overallNotes, setOverallNotes] = useState('');
    const [feedback, setFeedback] = useState({ rating: null, comments: '', crew_feedback: '', public_impact: null, weather: '' });
    const [record, setRecord] = useState(null);          // server checklist row
    const [dirty, setDirty] = useState(false);
    const [saveState, setSaveState] = useState({ saving: false, at: null, error: null });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [problems, setProblems] = useState([]);
    const [result, setResult] = useState(null);
    const [aiAnswer, setAiAnswer] = useState(null);
    const [isAsking, setIsAsking] = useState(false);
    const [showAi, setShowAi] = useState(true);

    const mountedRef = useRef(true);
    const saveChainRef = useRef(Promise.resolve());
    const recordRef = useRef(null);
    recordRef.current = record;
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const woId = workOrder?.id || null;
    const bKey = backupKey(roadId, woId);
    const readOnly = record?.status === 'submitted';
    const template = useMemo(() => templates?.find((t) => t.key === templateKey) || null, [templates, templateKey]);
    const summary = useMemo(() => (template ? scoreChecklist(template, responses, criticalCap) : null), [template, responses, criticalCap]);
    const advice = useMemo(() => checklistAdvice(template, summary), [template, summary]);

    // --- UPDATE (Inspection photos v1): field photos from the mobile app ────────
    const [photos, setPhotos] = useState([]);              // this checklist's photos
    const [jobPhotos, setJobPhotos] = useState([]);        // work order before / during / after
    const [photoState, setPhotoState] = useState({ loading: false, loaded: false, error: null, at: null });
    const [lightbox, setLightbox] = useState(null);        // { list: 'all' | 'job', index }
    const photoSeqRef = useRef(0);
    const photoChecklistId = record?.id || checklistId || null;

    const loadPhotos = useCallback(async ({ quiet = false } = {}) => {
        if (!isOpen || (!photoChecklistId && !woId)) return;
        const seq = ++photoSeqRef.current;
        if (!quiet) setPhotoState((s) => ({ ...s, loading: true, error: null }));
        const [ck, wo] = await Promise.allSettled([
            photoChecklistId ? apiFetch(`/roads/inspection-checklists/${encodeURIComponent(photoChecklistId)}/photos`) : Promise.resolve({ data: [] }),
            woId ? apiFetch(`/roads/work-orders/${encodeURIComponent(woId)}/photos`) : Promise.resolve({ data: [] }),
        ]);
        if (!mountedRef.current || seq !== photoSeqRef.current) return;   // a newer request won
        const ckList = ck.status === 'fulfilled' ? listOf(ck.value) : null;
        const woList = wo.status === 'fulfilled' ? listOf(wo.value) : null;
        if (ckList) setPhotos(ckList.slice().sort((a, b) => photoTime(a) - photoTime(b)));
        if (woList) {
            const ckIds = new Set((ckList || []).map((p) => p.id));
            // job photos only — the checklist's own photos also carry the work_order_id
            setJobPhotos(woList.filter((p) => !p.checklist_id && !ckIds.has(p.id)).sort((a, b) => photoTime(a) - photoTime(b)));
        }
        const err = ck.status === 'rejected' ? ck.reason : null;
        setPhotoState({
            loading: false,
            loaded: !!ckList || !photoChecklistId,
            error: err ? (err?.message || 'Could not load the inspection photos.') : null,
            at: new Date(),
        });
    }, [isOpen, photoChecklistId, woId]);

    // Reset when a different inspection opens
    useEffect(() => {
        setPhotos([]);
        setJobPhotos([]);
        setLightbox(null);
        setPhotoState({ loading: false, loaded: false, error: null, at: null });
    }, [photoChecklistId, woId, isOpen]);

    useEffect(() => { loadPhotos(); }, [loadPhotos]);

    // Live: the field app may still be uploading while the inspection is a draft
    const photoLoadRef = useRef(loadPhotos);
    photoLoadRef.current = loadPhotos;
    useEffect(() => {
        if (!isOpen || readOnly || !photoChecklistId) return undefined;
        const t = setInterval(() => {
            if (typeof document === 'undefined' || document.visibilityState !== 'hidden') photoLoadRef.current({ quiet: true });
        }, PHOTO_POLL_MS);
        return () => clearInterval(t);
    }, [isOpen, readOnly, photoChecklistId]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onFocus = () => photoLoadRef.current({ quiet: true });
        window.addEventListener('focus', onFocus);
        let timer = null;
        const onEvent = (msg) => {
            const p = msg?.data || msg?.payload || msg || {};
            const sameWo = woId && String(p.workOrderId ?? p.work_order_id ?? '') === String(woId);
            const sameRoad = roadId && String(p.road_id ?? '') === String(roadId);
            const sameCk = photoChecklistId && String(p.checklist_id ?? '') === String(photoChecklistId);
            if (!(sameWo || sameRoad || sameCk)) return;
            if (p.type && !/photo|checklist|inspection/i.test(String(p.type))) return;
            clearTimeout(timer);
            timer = setTimeout(() => photoLoadRef.current({ quiet: true }), 600);
        };
        const events = ['work_order_events', 'road_update'];
        try { events.forEach((e) => webSocketService?.subscribe?.(e, onEvent)); } catch { /* socket unavailable — polling covers it */ }
        return () => {
            window.removeEventListener('focus', onFocus);
            clearTimeout(timer);
            try { events.forEach((e) => webSocketService?.unsubscribe?.(e, onEvent)); } catch { /* ignore */ }
        };
    }, [isOpen, woId, roadId, photoChecklistId]);

    const itemLabels = useMemo(() => {
        const m = {};
        (template?.sections || []).forEach((s) => s.items.forEach((i) => { m[i.id] = i.label; }));
        return m;
    }, [template]);

    const photosByItem = useMemo(() => {
        const m = {};
        photos.forEach((p) => { if (p.item_id && itemLabels[p.item_id]) (m[p.item_id] ||= []).push(p); });
        return m;
    }, [photos, itemLabels]);

    const generalPhotos = useMemo(() => photos.filter((p) => !p.item_id || !itemLabels[p.item_id]), [photos, itemLabels]);

    const photoStats = useMemo(() => {
        const critIds = new Set(summary?.criticalFails || []);
        const failedIds = template ? template.sections.flatMap((s) => s.items).filter((i) => itemOutcome(i, responses[i.id]).status === 'fail') : [];
        const criticalNoPhoto = failedIds.filter((i) => (i.critical || critIds.has(i.id)) && !photosByItem[i.id]?.length);
        return {
            total: photos.length,
            items_with_photos: Object.keys(photosByItem).length,
            general: generalPhotos.length,
            job: jobPhotos.length,
            job_by_stage: jobPhotos.reduce((a, p) => { a[p.stage] = (a[p.stage] || 0) + 1; return a; }, {}),
            failed_items_with_photo: failedIds.filter((i) => photosByItem[i.id]?.length).length,
            failed_items: failedIds.length,
            criticalNoPhoto,
        };
    }, [photos, photosByItem, generalPhotos, jobPhotos, summary, template, responses]);

    const uploaderName = useCallback((id) => {
        if (id === null || id === undefined || id === '') return '—';
        const s = String(id);
        if (record && String(record.created_by_id ?? '') === s && (record.created_by_name || record.inspector_name)) return record.created_by_name || record.inspector_name;
        if (workOrder && String(workOrder.assigned_user_id ?? workOrder.assigned_to_id ?? '') === s && (workOrder.assigned_to_name || workOrder.assignee_name)) return workOrder.assigned_to_name || workOrder.assignee_name;
        return `User ${s}`;
    }, [record, workOrder]);

    // Lightbox lists: checklist photos (item order first, then general) and job photos
    const orderedPhotos = useMemo(() => {
        const out = [];
        (template?.sections || []).forEach((s) => s.items.forEach((i) => { (photosByItem[i.id] || []).forEach((p) => out.push(p)); }));
        return out.concat(generalPhotos);
    }, [template, photosByItem, generalPhotos]);
    const lightboxList = lightbox?.list === 'job' ? jobPhotos : orderedPhotos;
    const openPhoto = useCallback((p) => {
        const inJob = jobPhotos.findIndex((x) => x.id === p.id);
        if (inJob >= 0 && !orderedPhotos.some((x) => x.id === p.id)) { setLightbox({ list: 'job', index: inJob }); return; }
        const idx = orderedPhotos.findIndex((x) => x.id === p.id);
        setLightbox({ list: 'all', index: Math.max(0, idx) });
    }, [jobPhotos, orderedPhotos]);
    // --- END UPDATE ──────────────────────────────────────────────────────────

    // ── Load templates + resume a draft ──────────────────────────────────────
    const load = useCallback(async () => {
        if (!isOpen || !roadId) return;
        setIsLoading(true);
        setLoadError(null);
        setResult(null);
        setProblems([]);
        setAiAnswer(null);
        try {
            const t = await loadChecklistTemplates();
            if (!mountedRef.current) return;
            setTemplates(t.templates);
            setCriticalCap(t.criticalCap);
            let existing = null;
            if (checklistId) {
                const r = await apiFetch(`/roads/inspection-checklists/${encodeURIComponent(checklistId)}`);
                existing = r?.data || null;
            } else {
                const q = new URLSearchParams({ status: 'draft', limit: '1' });
                if (woId) q.set('work_order_id', woId);
                const r = await apiFetch(`/roads/${encodeURIComponent(roadId)}/inspection-checklists?${q.toString()}`).catch(() => null);
                const drafts = (r?.data || []).filter((d) => (woId ? String(d.work_order_id) === String(woId) : !d.work_order_id));
                if (drafts[0]) {
                    const full = await apiFetch(`/roads/inspection-checklists/${encodeURIComponent(drafts[0].id)}`).catch(() => null);
                    existing = full?.data || null;
                }
            }
            if (!mountedRef.current) return;
            if (existing) {
                setRecord(existing);
                setTemplateKey(existing.template_key);
                dispatch({ type: 'reset', value: existing.responses || {} });
                setOverallNotes(existing.overall_notes || '');
                setFeedback((f) => ({ ...f, ...(existing.feedback || {}) }));
            } else {
                const backup = readBackup(bKey);
                setRecord(null);
                setTemplateKey(backup?.templateKey || templateKeyFor(t.templates, category || workOrder?.category));
                dispatch({ type: 'reset', value: backup?.responses || {} });
                setOverallNotes(backup?.overallNotes || '');
                if (backup?.feedback) setFeedback((f) => ({ ...f, ...backup.feedback }));
                if (backup) toast({ title: 'Unsaved inspection restored', status: 'info', duration: 2500 });
            }
            setDirty(false);
        } catch (err) {
            if (mountedRef.current) setLoadError(err?.message || 'Could not load the inspection checklist.');
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isOpen, roadId, checklistId, woId, bKey, category, workOrder?.category, toast]);

    useEffect(() => { load(); }, [load]);

    // ── Save (create or update), serialised so two saves never race ──────────
    const persist = useCallback((submit = false) => {
        const run = async () => {
            const body = {
                template_key: templateKey,
                responses,
                overall_notes: overallNotes,
                feedback,
                work_order_id: woId || undefined,
                inspection_id: workOrder?.inspection_ref_id && workOrder?.inspection_source === 'road_inspection' ? workOrder.inspection_ref_id : undefined,
                category: workOrder?.category,
                ...(submit ? { status: 'submitted' } : {}),
            };
            const current = recordRef.current;
            const res = current?.id
                ? await apiFetch(`/roads/inspection-checklists/${encodeURIComponent(current.id)}`, { method: 'PUT', body: JSON.stringify(body) })
                : await apiFetch(`/roads/${encodeURIComponent(roadId)}/inspection-checklists`, { method: 'POST', body: JSON.stringify(body) });
            const saved = res?.data || null;
            if (saved && mountedRef.current) setRecord(saved);
            return { res, saved };
        };
        const p = saveChainRef.current.then(run, run);
        saveChainRef.current = p.catch(() => {});
        return p;
    }, [templateKey, responses, overallNotes, feedback, woId, workOrder?.inspection_ref_id, workOrder?.inspection_source, workOrder?.category, roadId]);

    // Local backup on every change; debounced server draft
    useEffect(() => {
        if (!dirty || readOnly || !template) return undefined;
        writeBackup(bKey, { templateKey, responses, overallNotes, feedback, at: Date.now() });
        const t = setTimeout(async () => {
            setSaveState((s) => ({ ...s, saving: true, error: null }));
            try {
                await persist(false);
                if (!mountedRef.current) return;
                setDirty(false);
                setSaveState({ saving: false, at: new Date(), error: null });
            } catch (err) {
                if (mountedRef.current) setSaveState({ saving: false, at: null, error: err?.message || 'Draft not saved — kept on this device.' });
            }
        }, AUTOSAVE_MS);
        return () => clearTimeout(t);
    }, [dirty, readOnly, template, bKey, templateKey, responses, overallNotes, feedback, persist]);

    const onItemChange = useCallback((id, patch) => { dispatch({ type: 'set', id, patch }); setDirty(true); }, []);
    const markSection = (section) => {
        dispatch({ type: 'bulk', ids: section.items.map((i) => i.id), status: 'pass' });
        setDirty(true);
    };

    const handleSubmit = async () => {
        if (!summary) return;
        const local = [];
        if (summary.unansweredCritical.length) local.push(`${summary.unansweredCritical.length} critical item(s) not checked yet.`);
        if (summary.missingNotes.length) local.push(`Add a note for every failed critical item (${summary.missingNotes.length} missing).`);
        if (!summary.answered) local.push('Check at least one item.');
        setProblems(local);
        if (local.length) return;
        setIsSubmitting(true);
        try {
            const { res, saved } = await persist(true);
            clearBackup(bKey);
            setDirty(false);
            const out = saved?.outcome || null;
            setResult({ summary: saved?.summary || summary, outcome: out, score: saved?.score ?? summary.score, rating: saved?.rating || summary.rating });
            publishBus(BUS_EVENTS.ROAD_CHANGED, { id: roadId, action: 'inspection_submitted', checklist_id: saved?.id });
            if (out?.auto_work_order?.workOrderId) publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { id: out.auto_work_order.workOrderId, action: 'created', road_id: roadId });
            toast({ title: 'Inspection submitted', description: res?.message, status: 'success', duration: 3000 });
            if (onSubmitted) onSubmitted(saved);
        } catch (err) {
            const serverProblems = err?.data?.problems || err?.response?.data?.problems;
            setProblems(serverProblems || [err?.message || 'Submission failed.']);
        } finally {
            if (mountedRef.current) setIsSubmitting(false);
        }
    };

    const askAi = async () => {
        if (!summary) return;
        setIsAsking(true);
        const ctx = buildRoadAiContext(null, { road: road || { id: roadId, name: roadName }, workOrder, checklistSummary: summary });
        ctx.inspection_template = template?.label;
        // --- UPDATE (Inspection photos v1): photo evidence counts (no image data sent)
        if (photoState.loaded) {
            ctx.inspection_photos = {
                total: photoStats.total,
                items_with_photos: photoStats.items_with_photos,
                general_photos: photoStats.general,
                failed_items_with_photo: photoStats.failed_items_with_photo,
                failed_critical_items_without_photo: photoStats.criticalNoPhoto.map((i) => i.label),
                job_photos_by_stage: woId ? photoStats.job_by_stage : undefined,
                captions: photos.map((p) => p.caption).filter(Boolean).slice(0, 15),
            };
        }
        // --- END UPDATE
        const q = `Review this ${template?.label || 'road inspection'} for ${roadName || 'the road'}. Summarise the defects, say whether the road / job is acceptable and list the follow-up work with priorities.`;
        try {
            const r = await askFlowSenseAi(q, ctx, 'road_works');
            if (!r?.answer) throw new Error('empty');
            if (mountedRef.current) setAiAnswer({ text: r.answer, source: 'ollama', model: r.model });
        } catch (e) {
            let lines = advice.map((a) => `• ${a.text}`).join('\n');
            // --- UPDATE (Inspection photos v1)
            if (photoState.loaded) {
                lines += `\n• Photos: ${photoStats.total} inspection photo(s)${woId ? `, ${photoStats.job} job photo(s)` : ''}.`;
                if (photoStats.criticalNoPhoto.length) lines += `\n• No photo for failed critical item(s): ${photoStats.criticalNoPhoto.map((i) => i.label).join(', ')}.`;
            }
            // --- END UPDATE
            if (mountedRef.current) setAiAnswer({ text: `${summary.score !== null ? `Score ${summary.score}/100 (${summary.rating}). ` : ''}${summary.failed} defect(s).\n${lines}`, source: 'rules', note: e?.message });
        } finally {
            if (mountedRef.current) setIsAsking(false);
        }
    };

    const createFollowUp = (f) => {
        const failed = (result?.summary || summary)?.failedItems?.filter((x) => (f.items || []).includes(x.label)) || [];
        const draft = roadFollowUpDraft({
            road: road || { id: roadId, name: roadName },
            followUp: f,
            parentWorkOrder: workOrder,
            checklistId: record?.id,
            priority: failed.some((x) => x.critical) ? 'high' : 'medium',
            description: [
                `Raised from the ${template?.label || 'road inspection'}${record?.id ? ` (checklist ${record.id})` : ''}.`,
                ...failed.map((x) => `- ${x.label}${x.value !== null ? ` (${x.value}${x.unit ? ` ${x.unit}` : ''})` : ''}${x.note ? `: ${x.note}` : ''}`),
            ].join('\n'),
        });
        onClose();
        openCreateWorkOrder(navigate, draft);
    };

    const scoreColor = summary?.rating ? RATING_COLOR[summary.rating] : 'gray';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={isMobile ? 'full' : '5xl'} scrollBehavior="inside" closeOnOverlayClick={!dirty}>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius={isMobile ? 0 : '2xl'}>
                <ModalHeader borderBottomWidth="1px" pb={3}>
                    <Flex justify="space-between" align="center" gap={3} wrap="wrap" pr={8}>
                        <Box>
                            <HStack spacing={2}>
                                <Text fontSize="md" fontWeight="800">Road inspection checklist</Text>
                                {readOnly && <Badge colorScheme="green"><Icon as={MdLock} mr={1} />Submitted</Badge>}
                                {!readOnly && record?.status === 'draft' && <Badge colorScheme="yellow">Draft</Badge>}
                            </HStack>
                            <Text fontSize="xs" color={muted} fontWeight="normal">
                                {roadName || roadId}{workOrder ? ` • WO ${workOrder.title || workOrder.id}` : ''}
                            </Text>
                        </Box>
                        <HStack spacing={2}>
                            <Select size="sm" maxW="260px" value={templateKey} onChange={(e) => { setTemplateKey(e.target.value); setDirty(true); }}
                                isDisabled={readOnly || !!record?.id || !templates} aria-label="Checklist template">
                                {(templates || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </Select>
                            {summary && (
                                <Tooltip hasArrow label={summary.criticalFails.length ? `Capped at ${criticalCap} by ${summary.criticalFails.length} critical defect(s)` : 'Weighted share of items that passed'}>
                                    <Badge colorScheme={scoreColor} fontSize="sm" px={2} py={1} aria-label={`Live score ${summary.score ?? 'none'}`}>
                                        {summary.score ?? '—'}{summary.rating ? ` · ${summary.rating}` : ''}
                                    </Badge>
                                </Tooltip>
                            )}
                        </HStack>
                    </Flex>
                    {summary && (
                        <Box mt={2}>
                            <Progress value={summary.completion} size="xs" borderRadius="full" colorScheme={summary.failed ? 'orange' : 'green'} aria-label={`${summary.completion}% checked`} />
                            <Text fontSize="2xs" color={muted} mt={1} fontWeight="normal">
                                {summary.answered}/{summary.total} checked · {summary.passed} OK · {summary.failed} defect(s) · {summary.na} N/A
                            </Text>
                        </Box>
                    )}
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody py={4}>
                    {isLoading && <Flex justify="center" py={10}><Spinner /></Flex>}
                    {!isLoading && loadError && (
                        <Alert status="error" borderRadius="lg">
                            <AlertIcon />
                            <AlertDescription flex={1}>{loadError}</AlertDescription>
                            <Button size="sm" leftIcon={<Icon as={MdRefresh} />} onClick={load}>Retry</Button>
                        </Alert>
                    )}

                    {!isLoading && !loadError && template && (
                        <Flex gap={4} direction={{ base: 'column', lg: 'row' }} align="flex-start">
                            {/* ── Checklist ─────────────────────────────────── */}
                            <Box flex="1" minW={0} w="100%">
                                {template.description && <Text fontSize="sm" color={muted} mb={3}>{template.description}</Text>}
                                {result && (
                                    <Alert status={(result.score ?? 100) >= 55 ? 'success' : 'warning'} borderRadius="lg" mb={3} alignItems="flex-start">
                                        <AlertIcon />
                                        <Box flex={1}>
                                            <Text fontWeight="700">Submitted — score {result.score ?? '—'}{result.rating ? ` (${result.rating})` : ''}</Text>
                                            {result.outcome?.assessment_id && <Text fontSize="sm">The road condition was updated.</Text>}
                                            {result.outcome?.auto_work_order?.workOrderId && (
                                                <Text fontSize="sm">FlowSense raised work order <b>{result.outcome.auto_work_order.workOrderId}</b> ({result.outcome.auto_work_order.category}).</Text>
                                            )}
                                            {result.outcome?.auto_work_order?.duplicateOf && <Text fontSize="sm">An open repair job ({result.outcome.auto_work_order.duplicateOf}) already covers this road.</Text>}
                                        </Box>
                                    </Alert>
                                )}
                                {problems.length > 0 && (
                                    <Alert status="warning" borderRadius="lg" mb={3}>
                                        <AlertIcon />
                                        <VStack align="stretch" spacing={0.5}>{problems.map((p) => <Text key={p} fontSize="sm">{p}</Text>)}</VStack>
                                    </Alert>
                                )}
                                <Accordion allowMultiple defaultIndex={template.sections.map((_, i) => i)}>
                                    {template.sections.map((section) => {
                                        const done = section.items.filter((i) => responses[i.id]?.status).length;
                                        const defects = section.items.filter((i) => itemOutcome(i, responses[i.id]).status === 'fail').length;
                                        const sectionPhotos = section.items.reduce((n, i) => n + (photosByItem[i.id]?.length || 0), 0); // --- UPDATE (Inspection photos v1)
                                        return (
                                            <AccordionItem key={section.key} border="none" mb={2}>
                                                <Flex align="center">
                                                    <AccordionButton px={2} borderRadius="md">
                                                        <Box flex="1" textAlign="left">
                                                            <Text fontWeight="800" fontSize="sm">{section.label}</Text>
                                                        </Box>
                                                        <HStack spacing={1} mr={2}>
                                                            <Badge fontSize="2xs">{done}/{section.items.length}</Badge>
                                                            {defects > 0 && <Badge colorScheme="red" fontSize="2xs">{defects} defect{defects > 1 ? 's' : ''}</Badge>}
                                                            {/* --- UPDATE (Inspection photos v1) */}
                                                            {sectionPhotos > 0 && <Badge colorScheme="purple" fontSize="2xs"><Icon as={MdPhotoCamera} mr={0.5} verticalAlign="-1px" />{sectionPhotos}</Badge>}
                                                            {/* --- END UPDATE */}
                                                        </HStack>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    {!readOnly && (
                                                        <Tooltip hasArrow label="Tick every unchecked item in this section as OK">
                                                            <IconButton size="xs" variant="ghost" icon={<Icon as={MdDoneAll} />} onClick={() => markSection(section)} aria-label={`Mark ${section.label} all OK`} />
                                                        </Tooltip>
                                                    )}
                                                </Flex>
                                                <AccordionPanel px={0} pb={2}>
                                                    <VStack align="stretch" spacing={2}>
                                                        {section.items.map((item) => (
                                                            <ChecklistItemRow key={item.id} item={item} response={responses[item.id]} onChange={onItemChange} readOnly={readOnly} colors={colors}
                                                                photos={photosByItem[item.id] || NO_PHOTOS} onOpenPhoto={openPhoto} photosLoaded={photoState.loaded} /* --- UPDATE (Inspection photos v1) */ />
                                                        ))}
                                                    </VStack>
                                                </AccordionPanel>
                                            </AccordionItem>
                                        );
                                    })}
                                </Accordion>

                                {/* --- UPDATE (Inspection photos v1): photo gallery ───────── */}
                                <Box mt={4} p={4} borderWidth="1px" borderColor={border} borderRadius="xl" bg={galleryBg} role="region" aria-label="Inspection photos">
                                    <Flex justify="space-between" align="center" mb={2} gap={2} wrap="wrap">
                                        <HStack spacing={2}>
                                            <Icon as={MdPhotoCamera} color="purple.400" />
                                            <Text fontWeight="800" fontSize="sm">Inspection photos</Text>
                                            <Badge colorScheme="purple" fontSize="2xs" data-testid="photo-total">{photoState.loaded ? photos.length : '—'}</Badge>
                                            {photoState.loaded && photos.length > 0 && (
                                                <Text fontSize="2xs" color={muted}>{photoStats.items_with_photos} item(s) · {photoStats.general} general</Text>
                                            )}
                                        </HStack>
                                        <HStack spacing={2}>
                                            {photoState.at && <Text fontSize="2xs" color={muted}>Updated {photoState.at.toLocaleTimeString()}</Text>}
                                            <Tooltip hasArrow label="Reload the photos uploaded from the field app">
                                                <IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} onClick={() => loadPhotos()} isLoading={photoState.loading}
                                                    isDisabled={!photoChecklistId && !woId} aria-label="Refresh photos" />
                                            </Tooltip>
                                        </HStack>
                                    </Flex>

                                    {photoState.error && (
                                        <Alert status="warning" borderRadius="md" py={2} fontSize="xs" mb={2}>
                                            <AlertIcon boxSize={3.5} />
                                            <AlertDescription flex={1}>{photoState.error}</AlertDescription>
                                            <Button size="xs" onClick={() => loadPhotos()}>Retry</Button>
                                        </Alert>
                                    )}
                                    {!photoChecklistId && (
                                        <Text fontSize="xs" color={muted}>Photos appear here once the inspection has been saved and the field app uploads them.</Text>
                                    )}
                                    {photoChecklistId && photoState.loading && !photoState.loaded && <Flex justify="center" py={4}><Spinner size="sm" /></Flex>}
                                    {photoChecklistId && photoState.loaded && photos.length === 0 && (
                                        <Text fontSize="xs" color={muted} data-testid="no-photos">
                                            No photos have been uploaded for this inspection yet.{!readOnly ? ' This list refreshes automatically while the inspection is a draft.' : ''}
                                        </Text>
                                    )}
                                    {photos.length > 0 && (
                                        <SimpleGrid columns={{ base: 3, sm: 4, md: 6 }} spacing={2} data-testid="photo-gallery">
                                            {orderedPhotos.map((p) => {
                                                const label = p.item_id ? itemLabels[p.item_id] : null;
                                                return (
                                                    <Box key={p.id} minW={0}>
                                                        <PhotoThumb photo={p} size="100%" onOpen={openPhoto} label={label || undefined} colors={colors} />
                                                        <Text fontSize="2xs" color={muted} noOfLines={1} mt={0.5} title={label || p.caption || 'General'}>
                                                            {label || p.caption || 'General'}
                                                        </Text>
                                                    </Box>
                                                );
                                            })}
                                        </SimpleGrid>
                                    )}

                                    {woId && jobPhotos.length > 0 && (
                                        <Box mt={3}>
                                            <HStack spacing={2} mb={1.5}>
                                                <Text fontWeight="700" fontSize="xs">Work order photos</Text>
                                                {Object.entries(photoStats.job_by_stage).map(([st, n]) => (
                                                    <Badge key={st} colorScheme={STAGE_COLOR[st] || 'gray'} fontSize="2xs">{STAGE_LABEL[st] || st} {n}</Badge>
                                                ))}
                                            </HStack>
                                            <SimpleGrid columns={{ base: 3, sm: 4, md: 6 }} spacing={2} data-testid="job-photo-gallery">
                                                {jobPhotos.map((p) => (
                                                    <PhotoThumb key={p.id} photo={p} size="100%" onOpen={openPhoto} badge={STAGE_LABEL[p.stage] || p.stage} label={p.caption || undefined} colors={colors} />
                                                ))}
                                            </SimpleGrid>
                                        </Box>
                                    )}
                                </Box>
                                {/* --- END UPDATE */}

                                {/* ── Notes & feedback ──────────────────────── */}
                                <Box mt={4} p={4} borderWidth="1px" borderColor={border} borderRadius="xl">
                                    <Text fontWeight="800" fontSize="sm" mb={2}>Inspector notes & feedback</Text>
                                    <Textarea size="sm" rows={3} value={overallNotes} onChange={(e) => { setOverallNotes(e.target.value); setDirty(true); }}
                                        placeholder="Overall observations, access issues, weather, photos taken…" isReadOnly={readOnly} maxLength={4000} aria-label="Overall notes" mb={3} />
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                        <Box>
                                            <Text fontSize="xs" fontWeight="700" mb={1}>Overall rating of the {workOrder ? 'work' : 'road'}</Text>
                                            <StarRating value={feedback.rating} onChange={(v) => { setFeedback((f) => ({ ...f, rating: v })); setDirty(true); }} readOnly={readOnly} />
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" fontWeight="700" mb={1}>Impact on the public</Text>
                                            <Select size="sm" value={feedback.public_impact || ''} onChange={(e) => { setFeedback((f) => ({ ...f, public_impact: e.target.value || null })); setDirty(true); }} isDisabled={readOnly} aria-label="Impact on the public">
                                                <option value="">Not assessed</option>
                                                <option value="none">None</option>
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High — safety risk</option>
                                            </Select>
                                        </Box>
                                        <Textarea size="sm" rows={2} value={feedback.comments || ''} onChange={(e) => { setFeedback((f) => ({ ...f, comments: e.target.value })); setDirty(true); }}
                                            placeholder="Feedback / recommendations" isReadOnly={readOnly} aria-label="Feedback" maxLength={2000} />
                                        <Textarea size="sm" rows={2} value={feedback.crew_feedback || ''} onChange={(e) => { setFeedback((f) => ({ ...f, crew_feedback: e.target.value })); setDirty(true); }}
                                            placeholder={workOrder ? 'Feedback for the crew / contractor' : 'Feedback for the maintenance team'} isReadOnly={readOnly} aria-label="Crew feedback" maxLength={2000} />
                                        <Input size="sm" value={feedback.weather || ''} onChange={(e) => { setFeedback((f) => ({ ...f, weather: e.target.value })); setDirty(true); }}
                                            placeholder="Weather / surface conditions" isReadOnly={readOnly} aria-label="Weather" maxLength={80} />
                                    </SimpleGrid>
                                </Box>
                            </Box>

                            {/* ── AI helper ─────────────────────────────────── */}
                            <Box w={{ base: '100%', lg: '320px' }} flexShrink={0} position={{ lg: 'sticky' }} top={0} bg={aiBg} borderRadius="xl" p={4} role="region" aria-label="Inspection AI helper">
                                <HStack justify="space-between" mb={2}>
                                    <HStack spacing={2}><Icon as={MdAutoAwesome} color="purple.400" /><Text fontWeight="800" fontSize="sm">FlowSense AI helper</Text></HStack>
                                    <Button size="xs" variant="ghost" onClick={() => setShowAi((v) => !v)}>{showAi ? 'Hide' : 'Show'}</Button>
                                </HStack>
                                <Collapse in={showAi} animateOpacity>
                                    <VStack align="stretch" spacing={2}>
                                        {advice.map((a) => (
                                            <Alert key={a.text} status={ADVICE_STATUS[a.level] || 'info'} borderRadius="md" py={2} px={3} fontSize="xs" alignItems="flex-start">
                                                <AlertIcon boxSize={3.5} mt={0.5} />
                                                <Box flex={1}>
                                                    <Text>{a.text}</Text>
                                                    {a.followUp && readOnly && (
                                                        <Button size="xs" mt={1} leftIcon={<Icon as={MdBuild} />} colorScheme="purple" variant="outline" onClick={() => createFollowUp(a.followUp)}>
                                                            Create {a.followUp.category}
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Alert>
                                        ))}
                                        {/* --- UPDATE (Inspection photos v1): photo evidence */}
                                        {photoState.loaded && photoStats.criticalNoPhoto.length > 0 && (
                                            <Alert status="warning" borderRadius="md" py={2} px={3} fontSize="xs" alignItems="flex-start" data-testid="ai-photo-warning">
                                                <AlertIcon boxSize={3.5} mt={0.5} />
                                                <Text>No photo uploaded for {photoStats.criticalNoPhoto.length} failed critical item(s): {photoStats.criticalNoPhoto.map((i) => i.label).join(', ')}. Ask the inspector to add one from the field app.</Text>
                                            </Alert>
                                        )}
                                        {photoState.loaded && photoStats.total > 0 && (
                                            <Text fontSize="2xs" color={muted}>
                                                <Icon as={MdPhotoCamera} mr={1} verticalAlign="-1px" />
                                                {photoStats.total} field photo(s){photoStats.failed_items ? ` · ${photoStats.failed_items_with_photo}/${photoStats.failed_items} defect(s) photographed` : ''}
                                            </Text>
                                        )}
                                        {/* --- END UPDATE */}
                                        {summary?.distressTypes?.length > 0 && (
                                            <Wrap spacing={1}>{summary.distressTypes.map((d) => <WrapItem key={d}><Tag size="sm" colorScheme="orange">{d.replace(/_/g, ' ')}</Tag></WrapItem>)}</Wrap>
                                        )}
                                        <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdAutoAwesome} />} onClick={askAi} isLoading={isAsking} loadingText="Asking…">
                                            Ask FlowSense AI to review
                                        </Button>
                                        {aiAnswer && (
                                            <Box p={3} borderRadius="md" bg="blackAlpha.50" aria-live="polite">
                                                <HStack justify="flex-end" mb={1}>
                                                    <Badge colorScheme={aiAnswer.source === 'ollama' ? 'purple' : 'gray'} fontSize="2xs">{aiAnswer.source === 'ollama' ? `Ollama${aiAnswer.model ? ` · ${aiAnswer.model}` : ''}` : 'FlowSense rules'}</Badge>
                                                </HStack>
                                                <Text fontSize="xs" whiteSpace="pre-wrap">{aiAnswer.text}</Text>
                                            </Box>
                                        )}
                                        {readOnly && !(result?.summary || summary)?.followUps?.length && (
                                            <Button size="xs" variant="outline" leftIcon={<Icon as={MdBuild} />} onClick={() => createFollowUp({ category: 'Road Condition Survey', type: 'inspection', items: [] })}>
                                                Schedule a re-inspection
                                            </Button>
                                        )}
                                    </VStack>
                                </Collapse>
                            </Box>
                        </Flex>
                    )}
                </ModalBody>

                <ModalFooter borderTopWidth="1px" gap={2} flexWrap="wrap">
                    <Text fontSize="xs" color={saveState.error ? 'orange.500' : muted} mr="auto" aria-live="polite">
                        {readOnly ? 'Submitted inspections are read-only.'
                            : saveState.saving ? 'Saving draft…'
                                : saveState.error ? saveState.error
                                    : saveState.at ? `Draft saved ${saveState.at.toLocaleTimeString()}`
                                        : dirty ? 'Unsaved changes' : ''}
                    </Text>
                    <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
                    {!readOnly && (
                        <>
                            <Button size="sm" leftIcon={<Icon as={MdSave} />} isDisabled={!template || saveState.saving} isLoading={saveState.saving}
                                onClick={async () => {
                                    try { await persist(false); setDirty(false); setSaveState({ saving: false, at: new Date(), error: null }); } catch (e) { setSaveState({ saving: false, at: null, error: e?.message || 'Draft not saved' }); }
                                }}>
                                Save draft
                            </Button>
                            <Button size="sm" colorScheme="blue" leftIcon={<Icon as={MdSend} />} onClick={handleSubmit} isLoading={isSubmitting} loadingText="Submitting…" isDisabled={!template}>
                                Submit inspection
                            </Button>
                        </>
                    )}
                </ModalFooter>
            </ModalContent>
            {/* --- UPDATE (Inspection photos v1) */}
            {lightbox && lightboxList.length > 0 && (
                <PhotoLightbox
                    photos={lightboxList}
                    index={Math.min(lightbox.index, lightboxList.length - 1)}
                    onClose={() => setLightbox(null)}
                    onIndex={(i) => setLightbox((l) => (l ? { ...l, index: i } : l))}
                    itemLabels={itemLabels}
                    uploaderName={uploaderName}
                    colors={colors}
                />
            )}
            {/* --- END UPDATE */}
        </Modal>
    );
}

RoadInspectionChecklist.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    roadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roadName: PropTypes.string,
    road: PropTypes.object,
    workOrder: PropTypes.object,
    checklistId: PropTypes.string,
    category: PropTypes.string,
    onSubmitted: PropTypes.func,
};
